/**
 * CR108: Enhanced prompt detection alert with routing to Prompt Packs
 */

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, MessageSquare, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PromptDetectionAlertProps {
  warning: string;
}

export function PromptDetectionAlert({ warning }: PromptDetectionAlertProps) {
  const navigate = useNavigate();

  return (
    <Alert variant="default" className="border-orange-500 bg-orange-500/10">
      <AlertTriangle className="h-4 w-4 text-orange-500" />
      <AlertTitle className="text-orange-700 dark:text-orange-300 text-sm font-semibold">
        This looks like a Prompt Pack prompt, not Rule Pack JSON
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <ul className="text-sm text-orange-700 dark:text-orange-300 list-disc list-inside space-y-1">
          <li><strong>Rule Packs</strong> are JSON configuration only (thresholds, field precedence, hard rules).</li>
          <li><strong>Prompt Packs</strong> contain AI prompts with role definitions and output contracts.</li>
        </ul>
        <p className="text-xs text-orange-600 dark:text-orange-400 italic">
          Detected: {warning}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2 border-orange-500 text-orange-700 hover:bg-orange-100 dark:text-orange-300 dark:hover:bg-orange-900/30"
          onClick={() => navigate("/admin/prompt-packs")}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Open Prompt Packs
          <ExternalLink className="h-3 w-3 ml-2" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// ================================================================
// ENHANCED PROMPT DETECTION FUNCTION
// ================================================================

export function detectPromptText(text: string): string | null {
  const trimmed = text.trim();
  const lowerText = trimmed.toLowerCase();
  
  // Common prompt indicators with specific explanations
  if (lowerText.startsWith("you are")) {
    return 'starts with "You are..." — a common AI prompt pattern';
  }
  if (lowerText.includes("output contract") || lowerText.includes("output_contract")) {
    return 'contains "OUTPUT CONTRACT" — an AI prompt instruction';
  }
  if (lowerText.includes("your task is") || lowerText.includes("your role is")) {
    return 'contains "your task/role is" — an AI role definition';
  }
  if (lowerText.includes("respond with") && lowerText.includes("json")) {
    return 'contains "respond with JSON" — an AI output instruction';
  }
  if (lowerText.includes("return this exact json") || lowerText.includes("return the following json")) {
    return 'contains JSON output instructions — an AI prompt pattern';
  }
  if (lowerText.includes("step 1:") && lowerText.includes("step 2:")) {
    return 'contains numbered steps — likely an AI instruction set';
  }
  if (lowerText.includes("you must") && lowerText.includes("you should")) {
    return 'contains instruction language ("you must/should") — an AI prompt pattern';
  }
  
  // Check if it doesn't start with { or [ (not JSON-like)
  if (trimmed.length > 0 && !trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return 'content does not start with { or [ — not valid JSON';
  }
  
  return null;
}
